import React, { useState } from 'react'
import { bindActionCreators } from 'redux'
import { useDispatch, useSelector } from 'react-redux'
import log from 'lib/log'
import { closeGroup, requestGroupAccess } from 'state/actions/groups'
import { loadGroupPosts, loadReferencesForGroup } from 'state/actions/posts'

import CircleSummaryData from './CircleSummary'
import CircleDesktopContent from './CircleDesktopContent'
import styles from 'circle/Circle.css'
import ExtraNav from '../../circle/CircleDesktopExtraNav'
import Dialog from 'components/Dialog'
import Button from 'components/Button'
import ErrorMessage from 'components/ErrorMessage'
import ImportantMessage from 'components/ImportantMessage'

import CircleDesktop from 'circle/CircleDesktop'
import { CircleContextProvider } from 'circle/CircleContextProvider'

import { GlobalIntroJsStyles } from 'core/GlobalIntroJsStyles'
import { useCircle, useCircleAccessRequest, useCircleUserInfo } from 'circle/circle.selectors'
import { useProfile } from 'profile'
import { AppComponentsContextProvider, CircleApiData, CircleModalDialogsContextProvider, CirclePage, Post, errorsReported, toast, useApi, useCircleContext } from 'lib-core'
import { createGroupOptions } from 'pages/groups/create/basics/CreateGroupBasicsPage'
import { CircleApiProvider } from './CircleApiProvider'
import { useInstance } from 'navigation/instance.selectors'
import { useLocation } from 'react-router-dom'
import { useInitialCirclePosts } from 'posts/posts.selectors'


const RequestAccessDialog = (props) => {
  if (props.hidden) {
    return null
  }

  const { onClose, busy, error, onRequestAccess } = props
  return (
    <Dialog title="group.request_access.title" onClose={onClose}>
      <div className={styles.close_dialog}>
        {error ? (
          <ErrorMessage
            error={`group.request_access.${(error && error.message) ||
              error}`}
          />
        ) : (
          <ImportantMessage i18nKey="group.request_access.description" />
        )}
        <div className={styles.buttons}>
          {!error && (
            <Button
              disabled={busy}
              label="group.request_access.request"
              onClick={onRequestAccess}
            />
          )}
          <Button
            disabled={busy}
            label="general.cancel"
            negative={true}
            onClick={onClose}
          />
        </div>
      </div>
    </Dialog>
  )
}

const CircleDesktopPage = () => {
  const location = useLocation()
  const pathname = location.pathname.split("/").filter(p => p)
  const circleId = pathname[pathname.length - 1]
  const { group: circle, membership, invitation } = useCircleUserInfo()
  const { profile } = useProfile()
  const requests = useCircleAccessRequest()
  const instance = useInstance()

  const initial: CircleApiData = {
    circle,
    // group, // equal to circle, for backward compatibility only
    profile,
    requests,
    membership,
    invitation,
    instance,
  }

  const initialPosts = useInitialCirclePosts()
  return (
    <AppComponentsContextProvider CircleApi={CircleApiProvider}>
      <CirclePage
        circleId={circleId}
        initial={initial}
        initialPosts={initialPosts}
      >
        <RequestAccess />
      </CirclePage>
    </AppComponentsContextProvider>

  )


  // return (
  //   <CircleContextProvider circle={circle} profile={profile} membership={membership} invitation={invitation} requests={requests}>
  //     <CircleApiProvider>
  //       <CircleModalDialogsContextProvider>
  //         <CircleDesktopPageInner />
  //       </CircleModalDialogsContextProvider>
  //     </CircleApiProvider>
  //   </CircleContextProvider>
  // )
}

const RequestAccess = (props) => {
  const desktopApi = useDesktopApi(props)
  const {
    group,
    upsertingAccessRequest,
    upsertAccessRequestError
  } = useSelector(selectForGroupPage)

  const { requestAccess, setRequestAccess } = useCircleContext()


  const hideRequestAccessDialog = () => {
    setRequestAccess(false)
  }

  const submitAccessRequest = errorsReported(async () => {
    const { requestGroupAccess } = desktopApi
    await requestGroupAccess({ group: group._id })
    hideRequestAccessDialog()
    toast("Access requested!")
  })

  return (
    <RequestAccessDialog
      hidden={!requestAccess}
      onRequestAccess={submitAccessRequest}
      onClose={hideRequestAccessDialog}
      busy={upsertingAccessRequest}
      error={upsertAccessRequestError}
    />
  )
}

const CircleDesktopPageInner = (props) => {
  const desktopApi = useDesktopApi(props)
  const {
    group,
    upsertingAccessRequest,
    upsertAccessRequestError
  } = useSelector(selectForGroupPage)

  const { requestAccess, setRequestAccess } = useCircleContext()

  const showRequestAccessDialog = () => {
    setRequestAccess(true)
  }
  const hideRequestAccessDialog = () => {
    setRequestAccess(false)
  }

  const submitAccessRequest = async () => {
    const { requestGroupAccess } = desktopApi
    try {
      await requestGroupAccess({ group: group._id })
      hideRequestAccessDialog()
    } catch (e) {
      log.error(e)
    }
  }

  return (
    <CircleDesktop
      title={null}
      right={
        <ExtraNav
          onRequestAccess={showRequestAccessDialog}
          searchAction={desktopApi.searchAction}
        />
      }
    >
      <GlobalIntroJsStyles />
      <div className={styles.group_page}>
        <RequestAccessDialog
          hidden={!requestAccess}
          onRequestAccess={submitAccessRequest}
          onClose={hideRequestAccessDialog}
          busy={upsertingAccessRequest}
          error={upsertAccessRequestError}
        />
        <CircleSummaryData onRequestAccess={showRequestAccessDialog} />
        <CircleDesktopContent />
      </div>
    </CircleDesktop>
  )
}

const selectForGroupPage = ({
  group: { upserting, upsertError, value: { group } = {} } = {},
  access_request: {
    upserting: upsertingAccessRequest,
    upsertError: upsertAccessRequestError,
  } = {}
}) => {
  return {
    group,
    upserting,
    upsertError,
    upsertingAccessRequest,
    upsertAccessRequestError
  }
}

const useDesktopApi = ({ place }) => {
  const dispatch = useDispatch()
  return {
    closeGroupWithOutcome: bindActionCreators(closeGroup, dispatch),
    requestGroupAccess: bindActionCreators(requestGroupAccess, dispatch),
    searchAction: place === 'groups/group/reference' ? loadReferencesForGroup : loadGroupPosts
  }
}

export default CircleDesktopPage
